<template>
   <div>
      <div v-if="!loaded">
         <SpinnerOverlay />
      </div>
      <b-container v-else class="study-setup">
         <PageTitle :title="`Welcome ${profile.firstName} ${profile.lastName}!`" />
         <div class="action-buttons-wrapper">
            <b-button
               v-if="isCustomer"
               size="sm"
               class="action-button dark-button"
               variant="primary"
               @click="onInviteUser()"
               >Invite User</b-button
            >
            <b-button
               v-if="activeStudyId && isActiveStudyInSetupStage"
               size="sm"
               class="action-button"
               variant="success"
               @click="toggleSubmission()"
               :disabled="activeStudy.studySetupCompleted"
               >Mark Section Complete</b-button
            >
         </div>
         <ManagerMessage>
            <template v-if="isActiveStudyInSetupStage">
               <template v-if="activeStudyId">
                  <h2 v-if="!activeStudy.studySetupCompleted" class="card-title">
                     Let's get your R&amp;D credit study started.
                  </h2>
                  <h2 v-else class="card-title">Thank you!</h2>
               </template>
               <h2 v-else class="card-title">We are still getting this page set up for you.</h2>

               <template v-if="activeStudyId">
                  <p v-if="!activeStudy.studySetupCompleted" class="card-description mb-0">
                     These are the categories of information we need to study your company's R&amp;D
                     tax benefit. Click start or edit for Onboard.tax® to guide you through each of
                     these. Or just upload files with the information we need and our team will take
                     care of the rest. Whatever is easiest for you. Mark this section complete when
                     you're done.
                  </p>
                  <!--                  <p v-if="!activeStudy.studySetupCompleted" class="card-description">-->
                  <!--                     Invite other users who can provide the info you don't have. Mark this area-->
                  <!--                     complete when you're done. Please feel free to reach me via the messaging-->
                  <!--                     interface (upper-right corner).-->
                  <!--                  </p>-->

                  <p v-if="activeStudy.studySetupCompleted" class="card-description mb-4">
                     You'll be notified about the next steps soon, please be on the lookout for an
                     email from us. If you have any questions don't hesitate to reach out, and if
                     you need to make changes, just cancel your submission and edit your information
                     again.
                  </p>
               </template>

               <p v-else class="card-description">
                  Onboard is not yet ready for you to provide us with your R&D information. Thank
                  you for your patience.
               </p>
            </template>

            <h2 v-if="!isActiveStudyInSetupStage" class="card-title">
               This is your study setup page.
            </h2>
            <p v-if="!isActiveStudyInSetupStage" class="card-description">
               The information we used to setup your study can be found here. You probably don't
               need to do anything else here, but if you have additional information or files to
               provide us for any of these sections, please feel free to do so. And as always, I'm
               available via messaging (upper right corner) if you have any questions.
            </p>
            <b-button
               v-if="activeStudyId && activeStudy.studySetupCompleted && isActiveStudyInSetupStage"
               size="sm"
               class="action-button dark-button"
               variant="primary"
               @click="toggleSubmission()"
               >Cancel Submission</b-button
            >
         </ManagerMessage>

         <b-alert variant="primary" :show="loaded && openStudies.length === 0">
            No open study exists for this company.
         </b-alert>

         <section v-if="activeStudyId" class="study-setup-section">
            <h4 class="section-title">
               Please provide the following for periods {{ periodsLabel }}
            </h4>
            <div class="cards-wrapper">
               <StudySetupCard
                  cardName="Project List"
                  cardTitle="Tell us about your R&D projects"
                  cardDescription="You can add your projects here, or provide a document that lists your project titles, descriptions, and years they were active."
                  progressDescription="Projects Added"
                  :progress="
                     studySetupProgress.projects && studySetupProgress.projects.cnt > 0
                        ? studySetupProgress.projects
                        : null
                  "
                  @onStart="onStartTrigger('projects')"
                  @onEdit="onEditTrigger('projects')"
               ></StudySetupCard>
               <StudySetupCard
                  v-if="isRndig || canEditPayroll"
                  cardName="Payroll"
                  cardTitle="Payroll / Employees"
                  cardDescription="Tell us about your employees and their wages by adding them or importing them from an Excel file."
                  progressDescription="Employees Added"
                  :progress="
                     studySetupProgress.employees && studySetupProgress.employees.cnt > 0
                        ? studySetupProgress.employees
                        : null
                  "
                  :completed="activeStudy.employees.completedAt !== null"
                  :checkmarkOnData="employeesHasDataForAllPeriods"
                  @onStart="onStartTrigger('employees')"
                  @onEdit="onEditTrigger('employees')"
               ></StudySetupCard>
               <StudySetupCard
                  v-if="isRndig || canEditContractors"
                  cardName="Contract Research"
                  cardTitle="Share your contractor details"
                  cardDescription="Tell us about any contractors you paid for R&D work by adding them here."
                  progressDescription="Contractors Added"
                  notUsedText="You are not using contract research for your R&D work."
                  :wasUsed="wasContractorsUsed"
                  :progress="
                     studySetupProgress.contractors && studySetupProgress.contractors.cnt > 0
                        ? studySetupProgress.contractors
                        : null
                  "
                  :completed="activeStudy.contractors.completedAt !== null"
                  :checkmarkOnData="contractorsHasDataForAllPeriods"
                  @onStart="onStartTrigger('contractors')"
                  @onEdit="onEditTrigger('contractors')"
               ></StudySetupCard>
               <StudySetupCard
                  cardName="Supplies Expenses"
                  cardTitle="Add your supply expenses"
                  cardDescription="Tell us if your company consumed supplies in your R&D work. You can upload a spreadsheet of supplies purchases or add supplies vendors manually."
                  progressDescription="Supplies Added"
                  :wasUsed="wasSuppliesUsed"
                  notUsedText="You are not using consumable supplies for your R&D work."
                  :progress="
                     studySetupProgress.supplies && studySetupProgress.supplies.cnt > 0
                        ? studySetupProgress.supplies
                        : null
                  "
                  :completed="activeStudy.supplies.completedAt !== null"
                  :checkmarkOnData="suppliesHasDataForAllPeriods"
                  @onStart="onStartTrigger('supplies')"
                  @onEdit="onEditTrigger('supplies')"
               ></StudySetupCard>
               <StudySetupCard
                  cardName="Cloud Computing Expenses"
                  cardTitle="Add your cloud computing expenses"
                  cardDescription="Tell us if your company paid for cloud computing related to R&D. You can upload a spreadsheet of payment records or add cloud computing vendors manually."
                  progressDescription="Vendors Added"
                  :wasUsed="wasCloudUsed"
                  notUsedText="You are not using cloud computing for your R&D work."
                  :progress="
                     studySetupProgress.cloudComputing && studySetupProgress.cloudComputing.cnt > 0
                        ? studySetupProgress.cloudComputing
                        : null
                  "
                  :completed="activeStudy.cloudcomputing.completedAt !== null"
                  :checkmarkOnData="cloudHasDataForAllPeriods"
                  @onStart="onStartTrigger('vendors')"
                  @onEdit="onEditTrigger('vendors')"
               ></StudySetupCard>
               <StudySetupCard
                  v-if="
                     (isRndig || canEditTaxReturns) &&
                     studySetupProgress.taxReturns &&
                     studySetupProgress.taxReturns.cnt > 0
                  "
                  cardName="Gross Receipts"
                  cardTitle="Tax Returns"
                  cardDescription="We need copies of your federal and state tax returns. Upload them here, and enter the values they contain."
                  :progress="
                     studySetupProgress.taxReturns && studySetupProgress.taxReturns.cnt > 0
                        ? studySetupProgress.taxReturns
                        : null
                  "
                  :completed="
                     studySetupProgress.taxReturns &&
                     studySetupProgress.taxReturns.cnt > 0 &&
                     studySetupProgress.taxReturns.completed === studySetupProgress.taxReturns.cnt
                  "
                  :disableEditWhenCompleted="false"
                  progressDescription="Periods Completed"
                  @onStart="onStartTrigger('tax-returns')"
                  @onEdit="onEditTrigger('tax-returns')"
               ></StudySetupCard>
            </div>
         </section>

         <section v-if="activeStudyId" class="study-setup-section">
            <h4 class="section-title">Other information we need</h4>
            <div class="cards-wrapper">
               <StudySetupCard
                  v-for="category in documentCategories"
                  :key="category.id"
                  cardName="Documents"
                  :cardTitle="category.name"
                  :cardDescription="category.description"
                  :otherInfoSection="true"
                  :progress="
                     category.summary && category.summary.length > 0
                        ? {docsCnt: category.summary.length}
                        : null
                  "
                  @onStart="onStartTrigger('other-info', category)"
                  @onEdit="onEditTrigger('other-info', category)"
               ></StudySetupCard>
            </div>
         </section>
         <InviteUserModal ref="inviteUserModal" />
         <UploadDocumentModal ref="uploadDocumentModal" />
         <template v-if="activeStudyId">
            <UtilizationModal
               expense-type="contractors"
               ref="utilizationContractorsModal"
               @afterClose="refreshCompanyData()"
               @onNextClick="onContractorsUtilizationModalNext()"
            />
            <ProjectChatbotModal ref="projectChatbotModal" @afterClose="refreshDashboardData()" />
            <TaxReturnEditModal ref="taxReturnModal" @afterClose="refreshDashboardData()" />
            <AddVendorModal @afterClose="refreshDashboardData()" expense-type="employees" />
            <AddVendorModal @afterClose="refreshDashboardData()" expense-type="contractors" />
            <AddVendorModal @afterClose="refreshDashboardData()" expense-type="cloud" />
            <AddVendorModal @afterClose="refreshDashboardData()" expense-type="supplies" />
            <UtilizationModal
               expense-type="cloud"
               ref="utilizationCloudModal"
               @afterClose="refreshCompanyData()"
               @onNextClick="onCloudUtilizationModalNext()"
            />
            <UtilizationModal
               expense-type="supplies"
               ref="utilizationSuppliesModal"
               @afterClose="refreshCompanyData()"
               @onNextClick="onSuppliesUtilizationModalNext()"
            />
         </template>
      </b-container>
   </div>
</template>

<script>
import {mapGetters} from 'vuex';
import StudySetupCard from '@/views/stages/widgets/StudySetupCard.vue';
import TaxReturnEditModal from '@/views/common/widgets/TaxReturnEditModal.vue';
import AddVendorModal from '@/views/expenses/components/AddVendorModal.vue';
import UploadDocumentModal from '@/views/stages/widgets/UploadDocumentModal.vue';
import InviteUserModal from '@/views/common/widgets/InviteUserModal.vue';
import SpinnerOverlay from '@/views/common/widgets/SpinnerOverlay';
import UtilizationModal from '../expenses/components/UtilizationModal.vue';

export default {
   name: 'StudySetup',
   components: {
      UtilizationModal,
      InviteUserModal,
      AddVendorModal,
      UploadDocumentModal,
      TaxReturnEditModal,
      StudySetupCard,
      SpinnerOverlay,
   },
   data() {
      return {
         loaded: false,
      };
   },

   computed: {
      ...mapGetters({
         profile: 'profile',
         isRndig: 'isRndig',
         isCustomer: 'isCustomer',
         isSME: 'isSME',
         currentUserPermissionFlags: 'users/currentUserPermissionFlags',
         activeStudyId: 'companies/activeStudyId',
         activeStudy: 'companies/activeStudy',
         isActiveStudyInSetupStage: 'companies/isActiveStudyInSetupStage',
         studySetupProgress: 'progress/studySetupData',
         currentCompany: 'companies/currentCompany',
         uploadCategories: 'uploads/uploadCategories',
         shouldOpenTaxReturnModal: 'shouldOpenTaxReturnModal',
      }),

      openStudies() {
         return this.currentCompany?.openStudies ?? [];
      },

      periodsLabel() {
         const labels = this.activeStudy.periods.map((period) => period.label);
         return labels.join(', ');
      },

      documentCategories() {
         return this.uploadCategories.filter((category) => !category.internal);
      },

      wasContractorsUsed() {
         if (!this.activeStudy) {
            return null;
         }
         return this.activeStudy.contractors.wasUsed;
      },

      wasSuppliesUsed() {
         if (!this.activeStudy) {
            return null;
         }
         const declaration = this.activeStudy.supplies.declared;
         if (declaration !== null) {
            return declaration === 'ONE' || declaration === 'MANY';
         }
         return null;
      },

      wasCloudUsed() {
         if (!this.activeStudy) {
            return null;
         }
         return this.activeStudy.cloudcomputing.wasUsed;
      },

      canEditTaxReturns() {
         return this.currentUserPermissionFlags.revenue;
      },

      canEditPayroll() {
         return this.currentUserPermissionFlags.payroll;
      },

      canEditContractors() {
         return this.currentUserPermissionFlags.contractorPayments;
      },

      employeesHasDataForAllPeriods() {
         const progress = this.studySetupProgress.employees;
         return (
            !!progress && progress.periodsCnt > 0 && progress.periodsWithEmp === progress.periodsCnt
         );
      },

      contractorsHasDataForAllPeriods() {
         return this.hasDataForAllPeriods(this.studySetupProgress.contractors);
      },

      suppliesHasDataForAllPeriods() {
         return this.hasDataForAllPeriods(this.studySetupProgress.supplies);
      },

      cloudHasDataForAllPeriods() {
         return this.hasDataForAllPeriods(this.studySetupProgress.cloudComputing);
      },
   },

   methods: {
      onInviteUser() {
         this.$refs.inviteUserModal.show();
      },
      toggleSubmission() {
         this.$store.dispatch('progress/toggleStudySetupCompletion', {
            studyId: this.activeStudyId,
            completed: !this.activeStudy.studySetupCompleted,
         });
      },
      onStartTrigger(modalName, category = null) {
         switch (modalName) {
            case 'projects':
               this.$refs.projectChatbotModal.show();
               break;
            case 'employees':
               this.$bvModal.show('employees-add-vendor-modal');
               break;
            case 'contractors':
               this.$refs.utilizationContractorsModal.show();
               break;
            case 'supplies':
               this.$refs.utilizationSuppliesModal.show();
               break;
            case 'vendors':
               this.$refs.utilizationCloudModal.show();
               break;
            case 'tax-returns':
               this.$refs.taxReturnModal.show();
               break;
            case 'other-info':
               this.$refs.uploadDocumentModal.show(category);
               break;
            default:
               return;
         }
      },
      onEditTrigger(modalName, category = null) {
         switch (modalName) {
            case 'projects':
               this.$refs.projectChatbotModal.show();
               break;
            case 'employees':
               this.$bvModal.show('employees-add-vendor-modal');
               break;
            case 'contractors':
               this.$refs.utilizationContractorsModal.show();
               break;
            case 'supplies':
               this.$refs.utilizationSuppliesModal.show();
               break;
            case 'vendors':
               this.$refs.utilizationCloudModal.show();
               break;
            case 'tax-returns':
               this.$refs.taxReturnModal.show();
               break;
            case 'other-info':
               this.$refs.uploadDocumentModal.show(category);
               break;
            default:
               return;
         }
      },
      onContractorsUtilizationModalNext() {
         this.$bvModal.show('contractors-add-vendor-modal');
         this.$refs.utilizationContractorsModal.hide();
      },
      onSuppliesUtilizationModalNext() {
         this.$bvModal.show('supplies-add-vendor-modal');
         this.$refs.utilizationSuppliesModal.hide();
      },
      onCloudUtilizationModalNext() {
         this.$bvModal.show('cloud-add-vendor-modal');
         this.$refs.utilizationCloudModal.hide();
      },
      refreshDashboardData() {
         this.$store.dispatch('progress/loadStudySetupProgress', {
            studyId: this.activeStudyId,
         });
      },
      refreshCompanyData() {
         this.$store.dispatch('companies/loadStudyAssignments', {
            companyId: this.currentCompany.id,
         });
      },

      hasDataForAllPeriods(progress) {
         return (
            !!progress &&
            progress.periodsCnt > 0 &&
            progress.periodsWithPayees === progress.periodsCnt
         );
      },
   },

   async created() {
      if (this.activeStudyId) {
         this.loaded = false;
         await this.$store.dispatch('progress/loadStudySetupProgress', {
            studyId: this.activeStudyId,
         });
      }
      this.loaded = true;
   },

   watch: {
      loaded(newVal) {
         if (newVal && this.shouldOpenTaxReturnModal) {
            this.$nextTick(() => {
               if (this.$refs.taxReturnModal) {
                  this.$refs.taxReturnModal.show();
                  this.$store.commit('setTaxReturnModalOpened', false);
               }
            });
         }
      },

      shouldOpenTaxReturnModal(newVal) {
         if (newVal && this.loaded) {
            this.$nextTick(() => {
               if (this.$refs.taxReturnModal) {
                  this.$refs.taxReturnModal.show();
                  this.$store.commit('setTaxReturnModalOpened', false);
               }
            });
         }
      },

      async activeStudyId() {
         if (this.activeStudyId) {
            this.loaded = false;
            await this.$store.dispatch('progress/loadStudySetupProgress', {
               studyId: this.activeStudyId,
            });
            this.loaded = true;
         }
      },
   },
};
</script>

<style scoped lang="scss">
.study-setup {
   background: #f4f4f4;
   padding: 0 1rem 0 4rem;
   max-width: 1260px;

   @media (max-width: 1240px) {
      margin-right: 3.125rem;
   }

   @media (max-width: 992px) {
      max-width: 760px;
      margin-right: 1rem;
   }
}

.action-buttons-wrapper {
   display: flex;
   justify-content: flex-end;
   gap: 1rem;
   padding-bottom: 1rem;
}

.action-button {
   width: 8.5rem;
   font-size: 0.75rem;
}

.card-title {
   font-size: 1.25rem;
   font-weight: 700;
}

.card-description {
   font-size: 0.8125rem;
}

.study-setup-section {
   padding-bottom: 2rem;
}

.section-title {
   font-size: 1.25rem;
   font-weight: 700;
   margin-bottom: 0.5rem;
}

.cards-wrapper {
   display: grid;
   grid-template-columns: 1fr 1fr 1fr;
   gap: 1rem;

   @media (min-width: 959px) and (max-width: 1060px) {
      grid-template-columns: 1fr 1fr;
   }
}
</style>
